// hooks/useLogger.ts
'use client';
import { useCallback } from 'react';
import * as Sentry from '@sentry/nextjs';

interface LogData {
  [key: string]: unknown;
}

export interface Logger {
  log: (message: string, data?: LogData) => void;
  info: (message: string, data?: LogData) => void;
  warn: (message: string, data?: LogData) => void;
  error: (message: string, data?: LogData) => void;
}

export function useLogger(componentName?: string): Logger {
  const sendLog = useCallback(async (
    level: 'log' | 'info' | 'warn' | 'error',
    message: string,
    data?: LogData
  ) => {
    // Always log to browser console
    console[level](message, data);

         try {
        if (level === 'error') {
          // For errors, create an exception in Sentry
          const error = new Error(message);
          error.name = componentName ? `${componentName}Error` : 'FrontendError';
          console.log("Sending error to Sentry:", error);

          Sentry.captureException(error, {
            extra: data,
            tags: {
              component: componentName,
              logLevel: level,
            },
            contexts: {
              logger: {
                component: componentName,
                url: typeof window !== 'undefined' ? window.location.href : undefined,
                userAgent: typeof window !== 'undefined' ? navigator.userAgent : undefined,
              }
            }
          });
        } else if (level === 'warn') {
          // For warnings, capture as message with warning level
          Sentry.captureMessage(message, {
            level: 'warning',
            extra: data,
            tags: {
              component: componentName,
              logLevel: level,
            }
          });
        } else {
          // For info/log, add as breadcrumb for context in future errors
          Sentry.addBreadcrumb({
            message,
            level: level === 'log' ? 'info' : level,
            category: componentName || 'frontend',
            data,
            timestamp: Date.now() / 1000,
          });
        }
      } catch (sentryError) {
        console.warn('Failed to send to Sentry:', sentryError);
      }
    
    // Send to API (only in production to avoid spam)
    if (process.env.NODE_ENV === 'production') {
      try {
        await fetch('/api/log', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            level,
            message,
            data,
            component: componentName,
            timestamp: new Date().toISOString(),
          }),
        });
      } catch (error) {
        // Silent fail - don't break the app if logging fails
        console.warn('Failed to send log to server:', error);
      }
    }
  }, [componentName]);

  return {
    log: (message: string, data?: LogData) => sendLog('log', message, data),
    info: (message: string, data?: LogData) => sendLog('info', message, data),
    warn: (message: string, data?: LogData) => sendLog('warn', message, data),
    error: (message: string, data?: LogData) => sendLog('error', message, data),
  };
}